import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  TextField,
  Link,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Cookies from "js-cookie";
import AuthLayout from "./AuthLayout";
import OtpInput from "./OtpInput";
import MfaSetup from "./MfaSetup";
const auth_service = require("../../helpers/auth_service");

// Pasos del flujo. Cognito decide cual toca segun el ChallengeName que
// devuelva; aqui solo se pinta el que corresponde.
//
//   CREDENTIALS  usuario + contrasena
//   NEW_PASSWORD contrasena definitiva (primer ingreso, FORCE_CHANGE_PASSWORD)
//   MFA_SETUP    QR de Google Authenticator + verificacion
//   MFA_CODE     6 digitos en los ingresos siguientes
const STEP = {
  CREDENTIALS: "CREDENTIALS",
  NEW_PASSWORD: "NEW_PASSWORD",
  MFA_SETUP: "MFA_SETUP",
  MFA_CODE: "MFA_CODE",
};

const PASSWORD_RULES =
  "Mínimo 8 caracteres, con mayúscula, minúscula, número y símbolo.";

export default function Login() {
  const [step, setStep] = useState(STEP.CREDENTIALS);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [session, setSession] = useState(null);
  const [secretCode, setSecretCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // El indicador muestra 2 puntos en un login normal y 3 cuando toca cambiar
  // contrasena o enrolar el MFA, para no prometer pasos que no van a ocurrir.
  const stepsForFlow =
    step === STEP.NEW_PASSWORD || step === STEP.MFA_SETUP
      ? ["Credenciales", "Contraseña", "Google Authenticator"]
      : ["Credenciales", "Verificación"];
  const activeStep = {
    CREDENTIALS: 0,
    NEW_PASSWORD: 1,
    MFA_SETUP: 2,
    MFA_CODE: 1,
  }[step];

  const fail = (err) => {
    console.log(err);
    setLoading(false);
    setError(
      err?.data?.message ||
        err?.mensaje ||
        "No se pudo completar la operación. Intente de nuevo."
    );
  };

  // Punto unico de salida: si Cognito ya entrego tokens se guarda la cookie y
  // se redirige; si devolvio otro challenge se avanza al paso que toque.
  const handleCognitoResponse = (data) => {
    if (data.AuthenticationResult) {
      Cookies.set(
        process.env.REACT_APP_COOKIE_KEY,
        JSON.stringify({
          TokenType: data.AuthenticationResult.TokenType,
          accessToken: data.AuthenticationResult.AccessToken,
          refreshToken: data.AuthenticationResult.RefreshToken,
        }),
        { domain: process.env.REACT_APP_DOMAIN }
      );
      window.location.href = process.env.REACT_APP_REDIRECT_ENDPOINT;
      return;
    }

    setSession(data.Session);
    setCode("");
    setLoading(false);

    switch (data.ChallengeName) {
      case "NEW_PASSWORD_REQUIRED":
        setStep(STEP.NEW_PASSWORD);
        break;
      case "MFA_SETUP":
        associateToken(data.Session);
        break;
      case "SOFTWARE_TOKEN_MFA":
        setStep(STEP.MFA_CODE);
        break;
      default:
        setError(
          `El servidor pidió una verificación no soportada (${data.ChallengeName}). Contacte al departamento IT.`
        );
    }
  };

  // Paso 1 — usuario y contraseña
  const submitCredentials = (event) => {
    event.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    setError(null);
    auth_service
      .postData("/usr/login", { username: username.trim(), password })
      .then((response) => handleCognitoResponse(response.data))
      .catch(fail);
  };

  // Paso 2 — contraseña definitiva en el primer ingreso
  const submitNewPassword = (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (newPassword.length < 8) {
      setError(PASSWORD_RULES);
      return;
    }
    setLoading(true);
    setError(null);
    auth_service
      .postData("/usr/respond-challenge", {
        username: username.trim(),
        challengeName: "NEW_PASSWORD_REQUIRED",
        session,
        newPassword,
      })
      .then((response) => handleCognitoResponse(response.data))
      .catch(fail);
  };

  // Paso 3a — Cognito genera la semilla TOTP para armar el QR
  const associateToken = (currentSession) => {
    setLoading(true);
    setError(null);
    auth_service
      .postData("/usr/associate-software-token", { session: currentSession })
      .then((response) => {
        setSecretCode(response.data.SecretCode);
        setSession(response.data.Session);
        setStep(STEP.MFA_SETUP);
        setLoading(false);
      })
      .catch(fail);
  };

  // Paso 3b — se verifica el código y recién ahí se cierra el challenge MFA_SETUP
  const submitMfaSetup = (event) => {
    if (event) event.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    setError(null);
    auth_service
      .postData("/usr/verify-software-token", { session, code })
      .then((response) =>
        auth_service.postData("/usr/respond-challenge", {
          username: username.trim(),
          challengeName: "MFA_SETUP",
          session: response.data.Session,
        })
      )
      .then((response) => handleCognitoResponse(response.data))
      .catch((err) => {
        // Cada intento consume la Session; si se agotó hay que reiniciar el login.
        fail(err);
        setCode("");
      });
  };

  // Paso 2 de los ingresos siguientes — solo los 6 dígitos
  const submitMfaCode = (event) => {
    if (event) event.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    setError(null);
    auth_service
      .postData("/usr/respond-challenge", {
        username: username.trim(),
        challengeName: "SOFTWARE_TOKEN_MFA",
        session,
        code,
      })
      .then((response) => handleCognitoResponse(response.data))
      .catch((err) => {
        fail(err);
        setCode("");
      });
  };

  const restart = (event) => {
    event.preventDefault();
    setStep(STEP.CREDENTIALS);
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setCode("");
    setSession(null);
    setSecretCode(null);
    setError(null);
  };

  return (
    <AuthLayout steps={stepsForFlow} activeStep={activeStep}>
      {step === STEP.CREDENTIALS && (
        <Header
          title="Iniciar sesión"
          subtitle="Ingresa tus credenciales del Portal EBG"
        />
      )}
      {step === STEP.NEW_PASSWORD && (
        <Header
          title="Crea tu contraseña"
          subtitle="Es tu primer ingreso, define una contraseña personal"
          username={username}
          onRestart={restart}
        />
      )}
      {step === STEP.MFA_SETUP && (
        <Header
          title="Activa Google Authenticator"
          subtitle="Escanea el código y confirma los 6 dígitos"
          username={username}
          onRestart={restart}
        />
      )}
      {step === STEP.MFA_CODE && (
        <Header
          title="Verifica tu identidad"
          subtitle="Escribe el código de Google Authenticator"
          username={username}
          onRestart={restart}
        />
      )}

      {error && (
        <Alert severity="error" variant="outlined" sx={{ fontSize: 13 }}>
          {error}
        </Alert>
      )}

      {step === STEP.CREDENTIALS && (
        <Box
          component="form"
          onSubmit={submitCredentials}
          sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
        >
          <Field
            label="Nombre de usuario"
            value={username}
            onChange={setUsername}
            autoFocus
            autoComplete="username"
            placeholder="usuario"
          />
          <Field
            label="Contraseña"
            value={password}
            onChange={setPassword}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder="••••••••"
            endAdornment={
              <PasswordToggle
                shown={showPassword}
                onToggle={() => setShowPassword((previous) => !previous)}
              />
            }
          />
          <SubmitButton
            loading={loading}
            disabled={!username.trim() || !password}
          >
            Continuar
          </SubmitButton>
        </Box>
      )}

      {step === STEP.NEW_PASSWORD && (
        <Box
          component="form"
          onSubmit={submitNewPassword}
          sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
        >
          <Field
            label="Contraseña nueva"
            value={newPassword}
            onChange={setNewPassword}
            type="password"
            autoComplete="new-password"
            autoFocus
            helperText={PASSWORD_RULES}
          />
          <Field
            label="Confirma la contraseña"
            value={confirmPassword}
            onChange={setConfirmPassword}
            type="password"
            autoComplete="new-password"
          />
          <SubmitButton
            loading={loading}
            disabled={!newPassword || !confirmPassword}
          >
            Guardar y continuar
          </SubmitButton>
        </Box>
      )}

      {step === STEP.MFA_SETUP && (
        <Box
          component="form"
          onSubmit={submitMfaSetup}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <MfaSetup secretCode={secretCode} username={username.trim()} />
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={() => submitMfaSetup()}
            disabled={loading}
          />
          <SubmitButton loading={loading} disabled={code.length !== 6}>
            Activar y entrar
          </SubmitButton>
        </Box>
      )}

      {step === STEP.MFA_CODE && (
        <Box
          component="form"
          onSubmit={submitMfaCode}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={() => submitMfaCode()}
            disabled={loading}
            autoFocus
          />
          <Typography sx={{ fontSize: 12, color: "#8ba3b8" }}>
            El código cambia cada 30 segundos en tu aplicación de autenticación.
          </Typography>
          <SubmitButton loading={loading} disabled={code.length !== 6}>
            Ingresar
          </SubmitButton>
        </Box>
      )}
    </AuthLayout>
  );
}

const Header = ({ title, subtitle, username, onRestart }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
    <Typography component="h2" sx={{ fontSize: 22, fontWeight: 700 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: "#8ba3b8" }}>
      {username ? (
        <>
          Hola <b style={{ color: "#e8eef4" }}>{username}</b> ·{" "}
          <Link href="#" onClick={onRestart} sx={{ color: "#6db9e8" }}>
            cambiar usuario
          </Link>
        </>
      ) : (
        subtitle
      )}
    </Typography>
    {username && (
      <Typography sx={{ fontSize: 13, color: "#8ba3b8" }}>{subtitle}</Typography>
    )}
  </Box>
);

const Field = ({ label, value, onChange, endAdornment, ...rest }) => (
  <TextField
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    size="small"
    fullWidth
    InputProps={
      endAdornment
        ? {
            endAdornment: (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ),
          }
        : undefined
    }
    {...rest}
  />
);

const PasswordToggle = ({ shown, onToggle }) => (
  <IconButton onClick={onToggle} edge="end" size="small" tabIndex={-1}>
    {shown ? (
      <VisibilityOff fontSize="small" />
    ) : (
      <Visibility fontSize="small" />
    )}
  </IconButton>
);

const SubmitButton = ({ loading, disabled, children }) => (
  <Button
    type="submit"
    variant="contained"
    disabled={loading || disabled}
    sx={{ py: 1.5, fontSize: 14.5, fontWeight: 700, borderRadius: "9px" }}
  >
    {loading ? <CircularProgress size={21} color="inherit" /> : children}
  </Button>
);
